import type { AuditLogRow, PdAccessLogRow } from '@/types/audit'
import type { Company } from '@/types/company'
import type { FeedEvent, Order, StatusHistoryRow } from '@/types/order'
import type { Profile } from '@/types/user'
import type { IdleSetting, RateK1, RateK2, RatesBase } from '@/types/tariffs'

import auditLogJson from '@/mocks/audit-log.json'
import chatMessagesJson from '@/mocks/chat-messages.json'
import clientDebugJson from '@/mocks/client-debug-events.json'
import companiesJson from '@/mocks/companies.json'
import eventsJson from '@/mocks/events.json'
import ordersJson from '@/mocks/orders.json'
import pdAccessLogJson from '@/mocks/pd-access-log.json'
import statusHistoryJson from '@/mocks/status-history.json'
import usersJson from '@/mocks/users.json'

function clone<T>(x: T): T {
  return structuredClone(x)
}

export const mockState = {
  orders: clone(ordersJson) as Order[],
  companies: clone(companiesJson) as Company[],
  profiles: clone(usersJson) as Profile[],
  statusHistory: clone(statusHistoryJson) as StatusHistoryRow[],
  auditLog: clone(auditLogJson) as AuditLogRow[],
  pdAccessLog: clone(pdAccessLogJson) as PdAccessLogRow[],
  events: clone(eventsJson) as FeedEvent[],
  chatMessages: clone(chatMessagesJson) as Array<{
    id: string
    order_id: string
    created_at: string
    author_name: string
    body: string
    attachments: { url: string; type: string }[]
  }>,
  clientDebug: clone(clientDebugJson) as Array<{
    user_id: string
    created_at: string
    payload: Record<string, unknown>
  }>,
  /** §6.1 TARIFFS_SPEC.md — стартовые значения базовых тарифов. */
  ratesBase: {
    id: 1,
    trip_customer: 3000,
    trip_inspector: 1000,
    inspection_customer: 5000,
    inspection_inspector: 2000,
    deal_customer: 15000,
    deal_inspector: 3000,
    delivery_customer: 5000,
    delivery_inspector: 2000,
    idle_customer: 2000,
    idle_inspector: 1000,
    updated_at: new Date().toISOString(),
  } as RatesBase,
  /** §6.2 TARIFFS_SPEC.md — К1 по городу. */
  ratesK1: [
    {
      id: 'k1-msk',
      city: 'Москва',
      coefficient: 1.0,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'k1-krd',
      city: 'Краснодар',
      coefficient: 0.8,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'k1-sam',
      city: 'Самара',
      coefficient: 0.7,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ] as RateK1[],
  /** §6.3 TARIFFS_SPEC.md — К2 по диапазону estimated_price. */
  ratesK2: [
    {
      id: 'k2-1',
      range_from: 0,
      range_to: 1.0,
      coefficient: 0.9,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'k2-2',
      range_from: 1.01,
      range_to: 3.0,
      coefficient: 1.0,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'k2-3',
      range_from: 3.01,
      range_to: 5.0,
      coefficient: 1.3,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'k2-4',
      range_from: 5.01,
      range_to: 8.0,
      coefficient: 1.5,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'k2-5',
      range_from: 8.01,
      range_to: null,
      coefficient: 2.0,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ] as RateK2[],
  /** §6.5 TARIFFS_SPEC.md — параметры простоя. */
  idleSettings: [
    {
      status: 'APPRAISAL',
      grace_minutes: 0,
      round_to_minutes: 60,
      updated_at: new Date().toISOString(),
    },
    {
      status: 'PAYMENT_TRANSFER',
      grace_minutes: 0,
      round_to_minutes: 60,
      updated_at: new Date().toISOString(),
    },
  ] as IdleSetting[],
}
